import { Alert, AlertDescription } from "@nimit9/signet-ui/components/alert"
import { Button } from "@nimit9/signet-ui/components/button"
import { Checkbox } from "@nimit9/signet-ui/components/checkbox"
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@nimit9/signet-ui/components/combobox"
import { Input } from "@nimit9/signet-ui/components/input"
import { Label } from "@nimit9/signet-ui/components/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nimit9/signet-ui/components/select"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useRef, useState } from "react"
import { Amount } from "@/components/common/amount"
import { flattenCategories, useCategories } from "@/hooks/use-categories"
import { useHousehold } from "@/hooks/use-household"
import { useCreateMerchant, useMerchants } from "@/hooks/use-merchants"
import { useOwnerLabels } from "@/hooks/use-owner-labels"
import { useCreatePerson, usePersons } from "@/hooks/use-persons"
import { usePropagateEntity } from "@/hooks/use-propagate-entity"
import type { TransactionRow } from "@/hooks/use-transactions"
import { api } from "@/lib/api"
import { MAX_DESCRIPTION_LENGTH } from "@/lib/constants"
import { prepareTransactionWrite } from "@/lib/crypto"
import { pickableCategories } from "@/lib/pickable-categories"
import { useCryptoStore } from "@/store/crypto"
import { useUIStore } from "@/store/ui"
import { ConflictPick } from "./conflict-pick"
import type { EntityPickerSelection } from "./entity-picker"
import { EntityPicker } from "./entity-picker"

const TYPE_ITEMS = { debit: "Debit", credit: "Credit" } as const
const CATEGORY_TYPE_ITEMS = {
  needs: "Needs",
  wants: "Wants",
  investments: "Investments",
  income: "Income",
  transfer: "Transfer",
} as const

// ─── Entity link state ────────────────────────────────────────────────────────

/** An existing entity that's already in the DB. */
type ExistingLink = { kind: "existing"; type: "merchant" | "person"; id: string }

/**
 * A "create" selection held pending until Save.
 * Nothing is written to the DB until the user actually saves.
 */
type PendingCreate = { kind: "pending-create"; type: "merchant" | "person"; name: string }

type EntityLinkState = ExistingLink | PendingCreate | null

// ─── Component ────────────────────────────────────────────────────────────────

export function TransactionDetail({ data }: { data: Record<string, unknown> }) {
  const txn = data.transaction as TransactionRow | undefined
  const { data: household } = useHousehold()
  // A partner's edit is no longer silent (UAT CF-09).
  const editedBy = editedByLabel(txn, household?.members ?? [])
  const closePanel = useUIStore((s) => s.closePanel)
  const queryClient = useQueryClient()
  const { data: catData } = useCategories()
  const ownerLabels = useOwnerLabels()
  const { data: merchantsResp } = useMerchants()
  const { data: personsResp } = usePersons()
  const createMerchant = useCreateMerchant()
  const createPerson = useCreatePerson()

  // Propagation hook — handles alias writes, computePropagation, bulk-link,
  // undo with snapshot restore, and 8s auto-dismiss timer.
  const propagateEntity = usePropagateEntity()

  const [form, setForm] = useState({
    date: txn?.date || "",
    amount: txn?.amount || "",
    type: txn?.type || "debit",
    description: txn?.description || "",
    owner: txn?.owner || "user_1",
    categorySlug: txn?.category?.slug || "",
    categoryType: (txn?.categoryType || txn?.category?.type || "") as string,
    notes: txn?.notes || "",
    referenceNumber: txn?.referenceNumber || "",
    isTransfer: txn?.isTransfer || false,
    isIgnored: txn?.isIgnored || false,
  })

  // Entity link — starts as ExistingLink (or null) from the txn's current state.
  // Becomes PendingCreate when the user types a new name; committed on Save.
  const [entityLink, setEntityLink] = useState<EntityLinkState>(
    txn?.merchant
      ? { kind: "existing", type: "merchant", id: txn.merchant.id }
      : txn?.personId
        ? { kind: "existing", type: "person", id: txn.personId }
        : null,
  )

  const [entityCreateError, setEntityCreateError] = useState<string | null>(null)

  // Ref tracking whether propagation occurred in the latest save — lets onSuccess
  // know whether to close the panel without reading stale closure state.
  const propagatedInSave = useRef(false)

  function handleEntitySelect(sel: EntityPickerSelection) {
    if (!txn) return
    if (sel.kind === "clear") {
      setEntityLink(null)
      setEntityCreateError(null)
      return
    }
    if (sel.kind === "existing") {
      setEntityLink({ kind: "existing", type: sel.entity.type, id: sel.entity.id })
      setEntityCreateError(null)
      return
    }
    // Deferred create — hold name in state, create in handleSave.
    // This prevents orphan entities when the user closes without saving.
    if (sel.kind === "create") {
      setEntityLink({ kind: "pending-create", type: sel.type, name: sel.name })
      setEntityCreateError(null)
    }
  }

  const mutation = useMutation({
    mutationFn: async (updates: Record<string, unknown>) => {
      // ── Step 1: If a create is pending, create the entity now ──────────────
      let resolvedEntityLink: ExistingLink | null = null
      const hasPendingCreate = entityLink?.kind === "pending-create"

      if (hasPendingCreate) {
        const pending = entityLink
        try {
          if (pending.type === "merchant") {
            const res = await createMerchant.mutateAsync({ cleanName: pending.name })
            resolvedEntityLink = { kind: "existing", type: "merchant", id: res.data.id }
          } else {
            const res = await createPerson.mutateAsync({ name: pending.name })
            resolvedEntityLink = { kind: "existing", type: "person", id: res.data.id }
          }
          setEntityLink(resolvedEntityLink)
          // Update updates with the newly created entity id.
          if (resolvedEntityLink.type === "merchant") {
            updates = { ...updates, merchantId: resolvedEntityLink.id, personId: null }
          } else {
            updates = { ...updates, personId: resolvedEntityLink.id, merchantId: null }
          }
        } catch (err) {
          setEntityCreateError(
            (err as Error)?.message || "Failed to create entity — please try again",
          )
          throw err
        }
      } else if (entityLink?.kind === "existing") {
        resolvedEntityLink = entityLink
      }

      // ── Step 2: Patch the source transaction ───────────────────────────────
      // Strip any internal sentinel keys before sending to the API.
      const { _hasPendingCreate: _hpc, ...apiUpdates } = updates as Record<string, unknown> & {
        _hasPendingCreate?: unknown
      }
      // Private mode: text fields are stored as client-side ciphertext, so encrypt
      // an edited description/notes/reference before it leaves the browser (the
      // create path does the same). Without the key, fail closed.
      const { key, mode } = useCryptoStore.getState()
      const body = await prepareTransactionWrite(apiUpdates, key, mode)
      const res = await api.patch(`/transactions/${txn?.id}`, body)

      // ── Step 3 + 4: Delegate to usePropagateEntity ────────────────────────
      const newCategoryId =
        "categoryId" in apiUpdates && typeof apiUpdates.categoryId === "string"
          ? apiUpdates.categoryId
          : (txn?.category?.id ?? null)

      const isNewMerchantLink =
        resolvedEntityLink?.type === "merchant" &&
        resolvedEntityLink.id !== (txn?.merchant?.id ?? null)
      const isNewPersonLink =
        resolvedEntityLink?.type === "person" && resolvedEntityLink.id !== (txn?.personId ?? null)

      if (resolvedEntityLink && (isNewMerchantLink || isNewPersonLink)) {
        const sourceDescription =
          (apiUpdates.description as string | undefined) ?? txn?.description ?? ""
        const entity =
          resolvedEntityLink.type === "merchant"
            ? ({ type: "merchant", merchantId: resolvedEntityLink.id } as const)
            : ({ type: "person", personId: resolvedEntityLink.id } as const)

        const didPropagate = await propagateEntity.propagate({
          sourceTxnId: txn?.id ?? "",
          sourceDescription,
          entity,
          categoryId: newCategoryId,
        })

        // If propagation produced matches, keep the panel open for the banner.
        if (didPropagate) {
          propagatedInSave.current = true
        }
      }

      return res
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transactions"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard"] })
      // Only close if propagation didn't produce a toast (the toast keeps the panel open).
      if (!propagatedInSave.current) {
        closePanel()
      }
      // Reset for next save.
      propagatedInSave.current = false
    },
  })

  if (!txn) return <p className="text-text-muted">Transaction not found</p>

  const isDebit = form.type === "debit"
  const allCategories = catData?.data ? flattenCategories(catData.data) : []

  // Derived lists for EntityPicker — local merchants only (with cleanName).
  const pickerMerchants = (merchantsResp?.data ?? [])
    .filter((m) => m.cleanName)
    .map((m) => ({ id: m.id, cleanName: m.cleanName as string }))
  const pickerPersons = (personsResp?.data ?? []).map((p) => ({ id: p.id, name: p.name }))
  const selectedCategoryId = allCategories.find((c) => c.slug === form.categorySlug)?.id ?? null
  const leafCategoryIds = [
    "_none",
    ...pickableCategories(
      allCategories.filter((c) => !c.children?.length),
      selectedCategoryId,
    ).map((c) => c.id),
  ]

  // Derive the value prop for EntityPicker from the current entityLink state.
  const entityPickerValue: { type: "merchant" | "person"; id: string } | null =
    entityLink?.kind === "existing"
      ? { type: entityLink.type, id: entityLink.id }
      : // For pending-create, show null (no ID yet) — the picker's own internal
        // query state will still show the typed name.
        null

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function handleSave() {
    if (!txn) return
    const updates: Record<string, unknown> = {}
    // The money values this edit started from: the server keeps both when someone changed one meanwhile.
    const base: Record<string, string> = {}
    if (form.date !== txn.date) {
      updates.date = form.date
      base.date = txn.date
    }
    if (form.amount !== txn.amount) {
      updates.amount = form.amount
      base.amount = txn.amount
    }
    if (form.type !== txn.type) {
      updates.type = form.type
      base.type = txn.type
    }
    if (Object.keys(base).length > 0) updates.base = base
    if (form.description !== txn.description) updates.description = form.description
    if (form.owner !== txn.owner) updates.owner = form.owner
    if (form.notes !== (txn.notes || "")) updates.notes = form.notes || null
    if (form.referenceNumber !== (txn.referenceNumber || ""))
      updates.referenceNumber = form.referenceNumber || null
    if (form.categorySlug !== (txn.category?.slug || "")) {
      const cat = allCategories.find((c) => c.slug === form.categorySlug)
      if (cat) updates.categoryId = cat.id
      else if (!form.categorySlug) updates.categoryId = null
    }
    const currentCategoryType = txn.categoryType || txn.category?.type || ""
    if (form.categoryType !== currentCategoryType) {
      updates.categoryType = form.categoryType || null
    }
    if (form.isTransfer !== txn.isTransfer) updates.isTransfer = form.isTransfer
    if (form.isIgnored !== txn.isIgnored) updates.isIgnored = form.isIgnored

    // Entity link changes.
    const prevMerchantId = txn.merchant?.id ?? null
    const prevPersonId = txn.personId ?? null

    if (entityLink?.kind === "existing") {
      if (entityLink.type === "merchant" && entityLink.id !== prevMerchantId) {
        updates.merchantId = entityLink.id
        updates.personId = null
      } else if (entityLink.type === "person" && entityLink.id !== prevPersonId) {
        updates.personId = entityLink.id
        updates.merchantId = null
      }
    } else if (entityLink?.kind === "pending-create") {
      // Mark the updates object non-empty so the mutation fires — the actual
      // entity create happens inside mutationFn (hasPendingCreate check).
      updates._hasPendingCreate = true
    } else if (!entityLink && (prevMerchantId || prevPersonId)) {
      // User cleared the link.
      updates.merchantId = null
      updates.personId = null
    }

    if (Object.keys(updates).length > 0) {
      propagatedInSave.current = false
      mutation.mutate(updates)
    } else {
      closePanel()
    }
  }

  const isBusy =
    mutation.isPending ||
    createMerchant.isPending ||
    createPerson.isPending ||
    propagateEntity.isUndoing

  return (
    <div className="space-y-5">
      <ConflictPick transactionId={txn.id} />
      {/* Amount preview */}
      <div className="text-center">
        <Amount
          value={isDebit ? -parseFloat(form.amount) : parseFloat(form.amount)}
          showSign
          className="text-[28px] font-medium"
          precise
        />
      </div>

      {/* Date + Type */}
      <div className="flex gap-3">
        <div className="flex-1 space-y-1.5">
          <Label className="text-[11px] text-text-muted">Date</Label>
          <Input type="date" value={form.date} onChange={(e) => update("date", e.target.value)} />
        </div>
        <div className="w-24 space-y-1.5">
          <Label className="text-[11px] text-text-muted">Type</Label>
          <Select
            value={form.type}
            onValueChange={(v) => v && update("type", v)}
            items={TYPE_ITEMS}
          >
            <SelectTrigger size="sm">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="debit">Debit</SelectItem>
              <SelectItem value="credit">Credit</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Amount */}
      <div className="space-y-1.5">
        <Label className="text-[11px] text-text-muted">Amount</Label>
        <Input
          type="number"
          step="0.01"
          value={form.amount}
          onChange={(e) => update("amount", e.target.value)}
        />
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <Label className="text-[11px] text-text-muted">Description</Label>
        <Input
          value={form.description}
          maxLength={MAX_DESCRIPTION_LENGTH}
          onChange={(e) => update("description", e.target.value)}
        />
        {txn?.unencrypted && (
          <p className="text-caption text-accent-amber">
            ⚠ This text is stored unencrypted on the server. If you didn't write it, it may not be
            yours. Editing the text saves it encrypted.
          </p>
        )}
      </div>

      {/* Entity picker */}
      <EntityPicker
        merchants={pickerMerchants}
        persons={pickerPersons}
        value={entityPickerValue}
        onChange={handleEntitySelect}
      />
      {entityCreateError && <p className="text-caption text-accent-red">{entityCreateError}</p>}
      {entityLink?.kind === "pending-create" && (
        <p className="text-[11px] text-text-hint">
          Will create {entityLink.type} "{entityLink.name}" on save
        </p>
      )}

      {/* Category + Type */}
      <div className="flex gap-3">
        <div className="flex-1 space-y-1.5">
          <Label className="text-[11px] text-text-muted">Category</Label>
          <Combobox
            items={leafCategoryIds}
            itemToStringLabel={(id) =>
              id === "_none"
                ? "Uncategorized"
                : (allCategories.find((c) => c.id === id)?.name ?? "")
            }
            value={selectedCategoryId}
            onValueChange={(id) => {
              if (!id || id === "_none") {
                update("categorySlug", "")
                return
              }
              const cat = allCategories.find((c) => c.id === id)
              if (cat) {
                update("categorySlug", cat.slug)
                update("categoryType", cat.type)
              }
            }}
          >
            <ComboboxInput
              placeholder="Uncategorized"
              showClear={!!form.categorySlug}
              className="h-7"
            />
            <ComboboxContent>
              <ComboboxEmpty>No categories found</ComboboxEmpty>
              <ComboboxList>
                {(catId) => {
                  if (catId === "_none") {
                    return (
                      <ComboboxItem key="_none" value="_none">
                        Uncategorized
                      </ComboboxItem>
                    )
                  }
                  const cat = allCategories.find((c) => c.id === catId)
                  if (!cat) return null
                  return (
                    <ComboboxItem key={catId} value={catId}>
                      {cat.icon && <span className="mr-0.5">{cat.icon}</span>}
                      {cat.name}
                    </ComboboxItem>
                  )
                }}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </div>
        <div className="w-32 space-y-1.5">
          <Label className="text-[11px] text-text-muted">Expense Type</Label>
          <Select
            value={form.categoryType || undefined}
            onValueChange={(v) => v && update("categoryType", v)}
            items={CATEGORY_TYPE_ITEMS}
          >
            <SelectTrigger size="sm">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {form.type === "debit" ? (
                <>
                  <SelectItem value="needs">Needs</SelectItem>
                  <SelectItem value="wants">Wants</SelectItem>
                  <SelectItem value="investments">Investments</SelectItem>
                </>
              ) : (
                <SelectItem value="income">Income</SelectItem>
              )}
              <SelectItem value="transfer">Transfer</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Owner */}
      <div className="w-full space-y-1.5">
        <Label className="text-[11px] text-text-muted">Owner</Label>
        <Select
          value={form.owner}
          onValueChange={(v) => v && update("owner", v)}
          items={ownerLabels}
        >
          <SelectTrigger size="sm">
            <SelectValue placeholder="Owner" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(ownerLabels).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Reference */}
      <div className="space-y-1.5">
        <Label className="text-[11px] text-text-muted">Reference number</Label>
        <Input
          placeholder="Optional"
          value={form.referenceNumber}
          onChange={(e) => update("referenceNumber", e.target.value)}
        />
      </div>

      {/* Notes */}
      <div className="space-y-1.5">
        <Label className="text-[11px] text-text-muted">Notes</Label>
        <textarea
          value={form.notes}
          onChange={(e) => update("notes", e.target.value)}
          placeholder="Add a note..."
          rows={2}
          className="w-full resize-none rounded-lg border border-border-default bg-bg-elevated px-3 py-2 text-[13px] text-text-primary placeholder:text-text-hint outline-none focus:border-accent-green"
        />
      </div>

      {/* Flags */}
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-[12px] text-text-secondary cursor-pointer">
          <Checkbox
            checked={form.isTransfer}
            onCheckedChange={(v) => update("isTransfer", v === true)}
          />
          Transfer
        </label>
        <label className="flex items-center gap-2 text-[12px] text-text-secondary cursor-pointer">
          <Checkbox
            checked={form.isIgnored}
            onCheckedChange={(v) => update("isIgnored", v === true)}
          />
          Ignored
        </label>
      </div>

      {/* Source info */}
      <p className="text-[11px] text-text-hint">
        Source: {txn.source.replace(/_/g, " ")}
        {txn.confidence && ` · Confidence: ${(parseFloat(txn.confidence) * 100).toFixed(0)}%`}
      </p>
      {editedBy && (
        <p data-testid="edited-by" className="text-caption text-text-hint">
          {editedBy}
        </p>
      )}

      {/* Propagation banner — ShadCN Alert, auto-dismisses after 8s */}
      {propagateEntity.result && propagateEntity.result.matchedIds.length > 0 && (
        <Alert>
          <AlertDescription className="flex items-center justify-between gap-3">
            <span>
              {propagateEntity.result.withCategory ? "Categorized" : "Linked"}{" "}
              <span className="font-medium">{propagateEntity.result.matchedIds.length}</span> other
              {propagateEntity.result.matchedIds.length === 1 ? "" : "s"}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[11px]"
                disabled={propagateEntity.isUndoing}
                onClick={propagateEntity.undo}
              >
                {propagateEntity.isUndoing ? "Undoing…" : "Undo"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[11px]"
                onClick={() => {
                  propagateEntity.dismiss()
                  closePanel()
                }}
              >
                Done
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Undo error */}
      {propagateEntity.undoError && (
        <p className="text-caption text-accent-red">{propagateEntity.undoError}</p>
      )}

      {/* Bulk-link fallback message — propagation failed after single-row succeeded */}
      {propagateEntity.result && propagateEntity.result.matchedIds.length === 0 && (
        <p className="text-[11px] text-text-hint">
          Linked this transaction. Run Auto-categorize to apply to others.
        </p>
      )}

      {/* Save / Close */}
      {propagateEntity.result ? (
        // After save+propagate: show Close instead of Save to avoid double-save.
        <Button
          variant="outline"
          onClick={() => {
            propagateEntity.dismiss()
            closePanel()
          }}
          className="w-full"
        >
          Close
        </Button>
      ) : (
        <Button onClick={handleSave} disabled={isBusy} className="w-full">
          {mutation.isPending || createMerchant.isPending || createPerson.isPending
            ? "Saving..."
            : "Save changes"}
        </Button>
      )}
    </div>
  )
}

/** "Edited by Ayushi · 2 Oct" for a hand-edited row, else null. */
export function editedByLabel(
  txn: Pick<TransactionRow, "updatedBy" | "updatedAt"> | undefined,
  members: Array<{ id: string; name: string | null; email: string }>,
): string | null {
  if (!txn?.updatedBy) return null
  const m = members.find((x) => x.id === txn.updatedBy)
  const who = m?.name || m?.email || "someone"
  const when = txn.updatedAt
    ? new Date(txn.updatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
    : ""
  return `Edited by ${who}${when ? ` · ${when}` : ""}`
}
